/**
 * 宠物篮球（P4-BASKET）：输入、画面、暂停、结算的骨架。规则全在 B 的 core（createBasketGame）；这里只把输入交进去、把状态画出来。
 * - 画面：Canvas 2D 画场地、篮架、球、TA、力度条与飘字；分数、连进、时间、暂停、开始页、暂停页、结算页都是 DOM，读屏念得到。
 * - 输入：←→ 或 A、D、下面的「往左」「往右」按住走；空格、↑ 或「投篮」按钮、在场地上按住＝蓄力，松开＝出手；Esc 暂停。
 * - 暂停：点暂停、按 Esc、页面切到后台都会暂停，按住的键一并松开；回来停在暂停页，点「继续」才接着玩。
 * - 结算：时间到出结算页（时间到时球还在飞，等它判完、照样算）；「不玩了」直接离开，不出结算页、这局不算成绩。成绩只留在这一局里，不存。
 * - TA 的样子：有这只宠物的篮球动作清单就按规则事件切段（basketMotion.ts），没有就画星球形象或物种插画，再没有画圆点小脸。
 * - 一只宠物一局：换了宠物整个重挂；形象、动作只认这只自己的，不串到别的宠物身上。
 * 接到哪个页面、路由怎么走由 I 接线；本组件只要一个 onExit。
 */
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { CharacterState, PetPrivateSummary } from "@/shared/contracts";
import { Button } from "@/shared/ui";
import { currentFrame, prepareMotion, referenceHeightOf, step as stepMotion, type MotionPlayer, type MotionSet } from "@/features/pet_motion";
import { createBasketGame, type BasketEvent, type BasketGame as Game, type BasketState } from "../core";
import { BASKET_ARENA, basketBallAt, basketSpritePlacement, drawBasketScene } from "./basketDraw";
import { basketAnnouncementFor, basketFloatsFor, tenseAfter } from "./basketFeedback";
import { applyBasketEvent, ballInPaw, settleAfterEnded, startBasketPlayer } from "./basketMotion";
import { readPalette, type Palette } from "./draw";
import { clockText, liveFloats, type FloatText } from "./feedback";
import { RESULT_DELAY_MS } from "./SnakeGame";
import { spriteFromCharacter, spriteFromFrame, spriteFromSpecies, withNaturalSize, type SpriteSpec } from "./sprite";
import { fitArena, type Fit } from "./viewport";
import "./snake.css";
import "./basket.css";

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const LEFT_KEYS = new Set(["ArrowLeft", "KeyA"]);
const RIGHT_KEYS = new Set(["ArrowRight", "KeyD"]);
const CHARGE_KEYS = new Set(["Space", "ArrowUp", "KeyW"]);

export interface BasketGameProps {
  pet: Pick<PetPrivateSummary, "pet_id" | "name" | "species">;
  character?: CharacterState | null;
  /** 这只宠物的篮球动作清单（purpose: game_basket）；没有就画静态图 */
  motion?: MotionSet | null;
  seed?: number;
  durationMs?: number;
  /** 开局站在哪（用例与演示台用）；产品页不传 */
  startX?: number;
  onExit: () => void;
  /** 不给就跟随系统“减少动态效果”设置 */
  reducedMotion?: boolean;
}

type Hud = { score: number; streak: number; clock: string };
type Held = { left: boolean; right: boolean; shoot: boolean; court: boolean };
const NOTHING_HELD: Held = { left: false, right: false, shoot: false, court: false };

function usePrefersReducedMotion(): boolean {
  const query = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION) : null;
  const [reduced, setReduced] = useState(() => query?.matches ?? false);
  useEffect(() => {
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, [query]);
  return reduced;
}

const hudOf = (state: BasketState): Hud => ({ score: state.score, streak: state.streak, clock: clockText(state.remainingMs) });

/**
 * 一只宠物一局：换了宠物（比如同一个页面换了路由参数）就整个重挂，分数、阶段、飘字、读屏、播放器都不带过去。
 * 同一只宠物重新下发（新对象、改了名字、形象或动作换了一版）不重开，这一局接着玩。
 */
export function BasketGame(props: BasketGameProps) {
  return <BasketRound key={props.pet.pet_id} {...props} />;
}

function BasketRound({ pet, character: givenCharacter = null, motion = null, seed, durationMs, startX, onExit, reducedMotion }: BasketGameProps) {
  // 只认这只宠物自己的形象；动作清单由 prepareMotion 拿这只的形象核身份（修订 D3）
  const character = givenCharacter?.pet_id === pet.pet_id ? givenCharacter : null;
  const systemReduced = usePrefersReducedMotion();
  const still = reducedMotion ?? systemReduced;
  const game = useMemo<Game>(
    () => createBasketGame({ seed: seed ?? Date.now() % 1_000_000, ...(durationMs !== undefined ? { durationMs } : {}), ...(startX !== undefined ? { startX } : {}) }),
    // 一个组件一局；再来一局用同一个对象 restart
    [],
  );
  const [phase, setPhase] = useState(game.state.phase);
  const [hud, setHud] = useState<Hud>(() => hudOf(game.state));
  const [showResult, setShowResult] = useState(false);
  const [notice, setNotice] = useState<{ seq: number; text: string }>({ seq: 0, text: "" });
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fitRef = useRef<Fit>(fitArena(0, 0, BASKET_ARENA.width, BASKET_ARENA.height));
  const dprRef = useRef(1);
  const paletteRef = useRef<Palette | null>(null);
  const keysRef = useRef(new Set<string>());
  const heldRef = useRef<Held>({ ...NOTHING_HELD });
  const floatsRef = useRef<FloatText[]>([]);
  const floatSeq = useRef(0);
  const tenseRef = useRef(false);
  /** 球最近一次不在手里时的位置：接球段从这里把球挪回爪里 */
  const looseRef = useRef<{ x: number; y: number } | null>(null);
  const images = useRef(new Map<string, HTMLImageElement>());
  const hudRef = useRef(hud);
  const stillRef = useRef(still);
  stillRef.current = still;

  // TA 的样子：篮球动作清单（能播才用）→ 星球形象 → 物种插画
  const prepared = useMemo(() => {
    const active = character?.active;
    return active && motion ? prepareMotion(motion, { set: active }) : null;
  }, [motion, character?.active?.pet_id, character?.active?.reference_version, character?.active?.assets.find((a) => a.pose === "neutral_full")?.content_sha256]);
  const playerRef = useRef<MotionPlayer | null>(null);
  useEffect(() => {
    playerRef.current = prepared ? startBasketPlayer(prepared) : null;
  }, [prepared]);
  const fallbackSprite = useMemo(() => spriteFromCharacter(character) ?? spriteFromSpecies(pet.species), [character, pet.species]);
  // 动画循环与键盘、后台监听只挂一次，读当前值一律经 ref
  const preparedRef = useRef(prepared);
  preparedRef.current = prepared;
  const fallbackRef = useRef(fallbackSprite);
  fallbackRef.current = fallbackSprite;
  const petNameRef = useRef(pet.name);
  petNameRef.current = pet.name;

  const loadImage = useCallback((url: string): HTMLImageElement | null => {
    if (typeof Image === "undefined") return null;
    let img = images.current.get(url);
    if (!img) {
      img = new Image();
      img.decoding = "async";
      img.src = url;
      images.current.set(url, img);
    }
    return img.complete && img.naturalWidth > 0 ? img : null;
  }, []);
  useEffect(() => {
    for (const clip of prepared?.clips ?? []) for (const f of clip.frames) loadImage(f.url);
    if (fallbackSprite) loadImage(fallbackSprite.url);
  }, [prepared, fallbackSprite, loadImage]);

  /** 此刻画哪张图、球画在哪 */
  const currentView = (): { sprite: { spec: SpriteSpec; image: CanvasImageSource; referenceContentHeight: number } | null; inPaw: { x: number; y: number } | null } => {
    const state = game.state;
    const player = playerRef.current;
    const motionNow = preparedRef.current;
    if (player && motionNow) {
      const frame = currentFrame(player);
      const image = loadImage(frame.url);
      if (image) {
        const spec = spriteFromFrame(frame, motionNow.set.canvas);
        const referenceContentHeight = referenceHeightOf(motionNow);
        const placement = basketSpritePlacement(spec, state.pet.x, state.floorY, referenceContentHeight);
        return { sprite: { spec, image, referenceContentHeight }, inPaw: ballInPaw(player, placement, looseRef.current) };
      }
    }
    const fallback = fallbackRef.current;
    if (fallback) {
      const image = loadImage(fallback.url);
      if (image) {
        const spec = withNaturalSize(fallback, image.naturalWidth, image.naturalHeight);
        return { sprite: { spec, image, referenceContentHeight: spec.contentHeight }, inPaw: null };
      }
    }
    return { sprite: null, inPaw: null };
  };

  const draw = (nowMs: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d") ?? null;
    if (!canvas || !ctx) return;
    paletteRef.current ??= readPalette(canvas);
    floatsRef.current = liveFloats(floatsRef.current, nowMs);
    const view = currentView();
    drawBasketScene(ctx, {
      state: game.state, fit: fitRef.current, dpr: dprRef.current, palette: paletteRef.current, sprite: view.sprite,
      ball: basketBallAt(game.state, view.inPaw), tense: tenseRef.current, floats: floatsRef.current, nowMs, reducedMotion: stillRef.current,
    });
    stageRef.current?.setAttribute("data-motion-clip", playerRef.current?.clip?.clip_key ?? "");
    stageRef.current?.setAttribute("data-motion-frame", String(playerRef.current?.frame ?? ""));
    stageRef.current?.setAttribute("data-motion-state", playerRef.current?.state ?? "");
    stageRef.current?.setAttribute("data-motion-pending", playerRef.current?.pending?.clip_key ?? "");
  };

  const handle = (events: BasketEvent[], nowMs: number) => {
    const state = game.state;
    for (const event of events) {
      floatsRef.current.push(...basketFloatsFor(event, state, nowMs, () => ++floatSeq.current));
      const text = basketAnnouncementFor(event, petNameRef.current);
      if (text) setNotice((n) => ({ seq: n.seq + 1, text }));
      tenseRef.current = tenseAfter(event, tenseRef.current);
      // 规则事件那一帧切段（起点对不上挂起、react 里来了回球与终局等帧 4）；分数只认规则核心
      if (playerRef.current) playerRef.current = applyBasketEvent(playerRef.current, event, state.pet.stance).player;
    }
    const next = hudOf(state);
    if (next.score !== hudRef.current.score || next.streak !== hudRef.current.streak || next.clock !== hudRef.current.clock) {
      hudRef.current = next;
      setHud(next);
    }
    setPhase(state.phase);
  };

  const inputNow = () => {
    const keys = keysRef.current;
    const held = heldRef.current;
    const left = held.left || [...LEFT_KEYS].some((k) => keys.has(k));
    const right = held.right || [...RIGHT_KEYS].some((k) => keys.has(k));
    const charge = held.shoot || held.court || [...CHARGE_KEYS].some((k) => keys.has(k));
    return { move: (right ? 1 : 0) - (left ? 1 : 0), charge };
  };
  const letGo = () => {
    keysRef.current.clear();
    heldRef.current = { ...NOTHING_HELD };
  };

  // 舞台尺寸：等比放下 360×640 的场地，画布按设备像素比出清晰的图
  useEffect(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!stage || !canvas) return undefined;
    const measure = () => {
      const rect = stage.getBoundingClientRect();
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      dprRef.current = dpr;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      fitRef.current = fitArena(rect.width, rect.height, BASKET_ARENA.width, BASKET_ARENA.height);
      draw(performance.now());
    };
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(stage);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // 主循环：只在进行中走；每帧先交输入再推进，事件当帧切段、飘字、播报
  useEffect(() => {
    if (phase !== "running" || typeof window.requestAnimationFrame !== "function") {
      draw(typeof performance !== "undefined" ? performance.now() : 0);
      return undefined;
    }
    let last: number | null = null;
    let handleId = 0;
    const tick = (now: number) => {
      if (last !== null) {
        const dt = now - last;
        game.setInput(inputNow());
        const events = game.advance(dt);
        if (game.state.ball.state !== "held") looseRef.current = { x: game.state.ball.x, y: game.state.ball.y };
        if (playerRef.current) playerRef.current = stepMotion(playerRef.current, dt).player;
        handle(events, now);
      }
      last = now;
      draw(now);
      if (game.state.phase === "running") handleId = window.requestAnimationFrame(tick);
    };
    handleId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(handleId);
  }, [phase]);

  // 结束以后：结尾动作再播一会儿，然后出结算页。还在空中、landed 一直没来的，一次起跳的时长后直接落到 END（F0 修订 G2）
  useEffect(() => {
    if (phase !== "ended" || !game.state.result?.counted) return undefined;
    if (still || typeof window.requestAnimationFrame !== "function") {
      // 减少动态效果：结尾不播动作，不等，当场落
      if (playerRef.current) playerRef.current = settleAfterEnded(playerRef.current, Number.POSITIVE_INFINITY).player;
      draw(performance.now());
      setShowResult(true);
      return undefined;
    }
    let handleId = 0;
    let last: number | null = null;
    const startedAt = performance.now();
    const tick = (now: number) => {
      if (last !== null && playerRef.current) playerRef.current = settleAfterEnded(stepMotion(playerRef.current, now - last).player, now - startedAt).player;
      last = now;
      draw(now);
      if (now - startedAt >= RESULT_DELAY_MS) setShowResult(true);
      else handleId = window.requestAnimationFrame(tick);
    };
    handleId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(handleId);
  }, [phase, still]);

  const pause = () => {
    letGo();
    handle(game.pause(), performance.now());
  };
  const resume = () => handle(game.resume(), performance.now());
  const start = () => handle(game.start(), performance.now());
  const again = () => {
    setShowResult(false);
    floatsRef.current = [];
    tenseRef.current = false;
    looseRef.current = null;
    letGo();
    playerRef.current = preparedRef.current ? startBasketPlayer(preparedRef.current) : null;
    handle(game.restart(Date.now() % 1_000_000), performance.now());
  };
  const leave = () => {
    handle(game.quit(), performance.now());
    onExit();
  };

  // 切到后台就暂停；回来停在暂停页
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && game.state.phase === "running") pause();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // 键盘：←→／A、D 走，空格／↑／W 按住蓄力、松开出手，Esc 暂停
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === "Escape" && game.state.phase === "running") {
        e.preventDefault();
        pause();
        return;
      }
      if (game.state.phase !== "running" || !(LEFT_KEYS.has(e.code) || RIGHT_KEYS.has(e.code) || CHARGE_KEYS.has(e.code))) return;
      e.preventDefault();
      keysRef.current.add(e.code);
    };
    const up = (e: KeyboardEvent) => keysRef.current.delete(e.code);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", letGo);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", letGo);
    };
  }, []);

  /** 按住式按钮（往左、往右、投篮）与场地：按下记住、松开或被打断就放开 */
  const hold = (which: keyof Held) => ({
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (game.state.phase !== "running") return;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      heldRef.current = { ...heldRef.current, [which]: true };
    },
    onPointerUp: () => { heldRef.current = { ...heldRef.current, [which]: false }; },
    onPointerCancel: () => { heldRef.current = { ...heldRef.current, [which]: false }; },
    onLostPointerCapture: () => { heldRef.current = { ...heldRef.current, [which]: false }; },
  });

  const result = game.state.result;
  return (
    <section className="ps-arcade ps-basket" aria-label="宠物篮球" data-phase={phase} data-score={hud.score}>
      <header className="ps-arcade__hud">
        <span className="ps-arcade__score" aria-label={`${hud.score} 分`}>{hud.score}<small>分</small></span>
        {hud.streak >= 2 ? <span className="ps-basket__streak">连进 {hud.streak}</span> : null}
        <span className="ps-arcade__clock" aria-label={`还剩 ${hud.clock.replace(":", " 分 ")} 秒`}>{hud.clock}</span>
        <Button variant="ghost" size="sm" icon="pause" aria-label="暂停" onClick={pause} disabled={phase !== "running"} className="ps-arcade__pause" />
      </header>
      <div className="ps-arcade__stage" ref={stageRef}>
        <canvas ref={canvasRef} className="ps-arcade__canvas" role="img" aria-label={`${pet.name}在球场上，${hud.score} 分`} {...hold("court")} />
        {phase === "ready" ? (
          <div className="ps-arcade__overlay" role="dialog" aria-modal="true" aria-labelledby="ps-basket-title">
            <div className="ps-arcade__sheet">
              <h2 id="ps-basket-title">宠物篮球</h2>
              <p className="ps-arcade__line">带{pet.name}练投篮</p>
              {/* 一句一行、每句短到最窄的屏也放得下，不折出孤字 */}
              <ul className="ps-arcade__rules">
                <li>左右走到想投的地方。</li>
                <li>按住蓄力，力度来回摆。</li>
                <li>松手出手，进了得分。</li>
                <li>三分线外投进得 3 分。</li>
                <li>空心、连进都有加分。</li>
              </ul>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="play" onClick={start} autoFocus>开始</Button>
                <Button variant="ghost" onClick={onExit}>回去</Button>
              </div>
            </div>
          </div>
        ) : null}
        {phase === "paused" ? (
          <div className="ps-arcade__overlay" role="dialog" aria-modal="true" aria-labelledby="ps-basket-paused">
            <div className="ps-arcade__sheet">
              <h2 id="ps-basket-paused">暂停中</h2>
              <p className="ps-arcade__line">{hud.score} 分 · 还剩 {hud.clock}</p>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="play" onClick={resume} autoFocus>继续</Button>
                <Button variant="ghost" onClick={leave}>不玩了</Button>
              </div>
            </div>
          </div>
        ) : null}
        {phase === "ended" && showResult && result?.counted ? (
          <div className="ps-arcade__overlay is-result" role="dialog" aria-modal="true" aria-labelledby="ps-basket-result">
            <div className="ps-arcade__sheet">
              <h2 id="ps-basket-result">时间到</h2>
              <p className="ps-arcade__total"><strong>{result.score}</strong> 分</p>
              <dl className="ps-arcade__stats">
                <div><dt>投了</dt><dd>{result.shots} 次</dd></div>
                <div><dt>进了</dt><dd>{result.makes} 个</dd></div>
                <div><dt>三分球</dt><dd>{result.threes} 个</dd></div>
                <div><dt>空心</dt><dd>{result.swishes} 个</dd></div>
                <div><dt>最长连进</dt><dd>{result.bestStreak} 个</dd></div>
              </dl>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="refresh" onClick={again} autoFocus>再来一局</Button>
                <Button variant="ghost" onClick={onExit}>回去</Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {/* 按住式按钮放在场地下面一行，不盖住地上的 TA；一直在，不在进行中时置灰，场地不跟着跳 */}
      <div className="ps-basket__controls">
        <Button variant="secondary" icon="back" aria-label="往左走" className="ps-basket__move" disabled={phase !== "running"} {...hold("left")} />
        <Button variant="primary" className="ps-basket__shoot" aria-label="投篮：按住蓄力，松手出手" disabled={phase !== "running"} {...hold("shoot")}>投篮</Button>
        <Button variant="secondary" icon="chevron" aria-label="往右走" className="ps-basket__move" disabled={phase !== "running"} {...hold("right")} />
      </div>
      <p className="visually-hidden" aria-live="polite">
        <span key={notice.seq}>{notice.text}</span>
      </p>
    </section>
  );
}
